import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import "./checkout.css";

export const CheckoutPage = ({
  createOrder,
  order,
  isLoading,
  isConfirmed,
  confirmOrder,
  confirmedOrder,
  loggedUser,
  cart,
  applyCoupon,
  coupon,
  createOrderCOD,
  resetOrderState,
  fetchOrders,
  orders,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const pendingPayment = useRef(false);
  const pendingPricings = useRef(null);
  const [isPaymentOn, setIsPaymentOn] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [isShipping, setIsShipping] = useState(false);
  const [pricings, setPricings] = useState({
    subTotal: 0,
    discount: 0,
    shippingCharge: 0,
    totalAmount: 0,
    quantity: 0,
    gstAmount: 0,
    discountPercent: 0,
  });
  const [codeErr, setCodeErr] = useState(null);

  useEffect(() => {
    if (loggedUser?.email && fetchOrders) {
      fetchOrders();
    }
  }, [loggedUser, fetchOrders]);

  useEffect(() => {
    if (loggedUser?.email) {
      setValue("billingName", `${loggedUser.firstName || ""} ${loggedUser.lastName || ""}`.trim());
      setValue("billingEmail", loggedUser.email || "");
      setValue("billingPhone", loggedUser.phone || "");
    }
  }, [loggedUser, setValue]);

  const handleUseLastAddress = (checked) => {
    if (checked && orders && orders.length > 0) {
      const latestOrder = orders[0];
      setValue("billingName", latestOrder.billingName || "");
      setValue("billingEmail", latestOrder.billingEmail || "");
      setValue("billingPhone", latestOrder.billingPhone || "");
      setValue("billingAddress", latestOrder.billingAddress || "");
      setValue("billingCity", latestOrder.billingCity || "");
      setValue("billingState", latestOrder.billingState || "");
      setValue("billingCountry", latestOrder.billingCountry || "India");
      setValue("billingZip", latestOrder.billingZip || "");

      if (latestOrder.shippingAddress && latestOrder.shippingAddress !== latestOrder.billingAddress) {
        setIsShipping(true);
        setValue("shippingName", latestOrder.shippingName || "");
        setValue("shippingEmail", latestOrder.shippingEmail || "");
        setValue("shippingPhone", latestOrder.shippingPhone || "");
        setValue("shippingAddress", latestOrder.shippingAddress || "");
        setValue("shippingCity", latestOrder.shippingCity || "");
        setValue("shippingState", latestOrder.shippingState || "");
        setValue("shippingCountry", latestOrder.shippingCountry || "India");
        setValue("shippingZip", latestOrder.shippingZip || "");
      } else {
        setIsShipping(false);
      }
    } else {
      setValue("billingAddress", "");
      setValue("billingCity", "");
      setValue("billingState", "");
      setValue("billingZip", "");
      setValue("billingCountry", "India");

      setIsShipping(false);
      setValue("shippingName", "");
      setValue("shippingEmail", "");
      setValue("shippingPhone", "");
      setValue("shippingAddress", "");
      setValue("shippingCity", "");
      setValue("shippingState", "");
      setValue("shippingCountry", "India");
      setValue("shippingZip", "");

      if (loggedUser?.email) {
        setValue("billingName", `${loggedUser.firstName || ""} ${loggedUser.lastName || ""}`.trim());
        setValue("billingEmail", loggedUser.email || "");
        setValue("billingPhone", loggedUser.phone || "");
      }
    }
  };

  useEffect(() => {
    if (confirmedOrder?.id) {
      localStorage.removeItem("affiliateRefId");
      localStorage.removeItem("autoCouponCode");
      navigate(`/order-success/${confirmedOrder.id}`);
    }
  }, [confirmedOrder, navigate]);

  useEffect(() => {
    return () => {
      resetOrderState();
    };
  }, [resetOrderState]);

  // Automatically apply, re-apply, or validate coupon when subTotal changes
  useEffect(() => {
    const activeCouponCode = coupon?.code || localStorage.getItem("autoCouponCode");
    if (pricings.subTotal < 500) {
      if (coupon?.code) {
        resetOrderState();
        setCouponCode("");
      }
    } else if (activeCouponCode) {
      applyCoupon({
        code: activeCouponCode,
        amount: pricings.subTotal,
      });
      setCouponCode(activeCouponCode);
    }
  }, [pricings.subTotal, applyCoupon, resetOrderState]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePayment = useCallback(
    (currentOrder, currentPricings) => {
      if (!currentOrder?.payment_session_id) {
        console.error("Cashfree Error: Missing payment_session_id", currentOrder);
        alert("Order creation failed. Please try again.");
        return;
      }

      console.log("Initializing Cashfree checkout session:", currentOrder.payment_session_id);

      try {
        const cashfree = window.Cashfree({
          mode: process.env.REACT_APP_CASHFREE_MODE || "sandbox",
        });

        cashfree.checkout({
          paymentSessionId: currentOrder.payment_session_id,
          redirectTarget: "_self",
        });
      } catch (err) {
        console.error("Cashfree initialization error:", err);
        alert("Could not load payment checkout. Please refresh the page.");
      }
    },
    [],
  );

  // Trigger Cashfree when order arrives
  useEffect(() => {
    if (order?.payment_session_id && pendingPayment.current) {
      pendingPayment.current = false;
      handlePayment(order, pendingPricings.current);
    }
  }, [order, handlePayment]);

  function onSubmit(data) {
    if (pricings.subTotal < 500 && (coupon?.code || couponCode)) {
      alert("Minimum order amount of ₹500 is required to apply a coupon.");
      resetOrderState();
      setCouponCode("");
      return;
    }

    if (data.orderType) {
      if (!pricings.totalAmount || pricings.totalAmount <= 0) {
        alert("Order amount must be greater than ₹0.");
        return;
      }

      const shippingData = {
        shippingName: !isShipping ? data.billingName : data?.shippingName,
        shippingEmail: !isShipping ? data.billingEmail : data?.shippingEmail,
        shippingPhone: !isShipping ? data.billingPhone : data?.shippingPhone,
        shippingAddress: !isShipping
          ? data.billingAddress
          : data?.shippingAddress,
        shippingCity: !isShipping ? data.billingCity : data?.shippingCity,
        shippingState: !isShipping ? data.billingState : data?.shippingState,
        shippingCountry: !isShipping
          ? data.billingCountry
          : data?.shippingCountry,
        shippingZip: !isShipping ? data.billingZip : data?.shippingZip,
      };

      const refAff = localStorage.getItem("affiliateRefId");
      const hasCoupon = !!coupon?.code && pricings.subTotal >= 500;
      const calculatedReward = hasCoupon ? (coupon?.affiliateReward || 0) : (refAff && pricings.subTotal >= 500 ? parseFloat((pricings.subTotal * 0.10).toFixed(2)) : 0);
      const calculatedPoints = hasCoupon ? (coupon?.affiliatePoint || 0) : (refAff && pricings.subTotal >= 500 ? parseFloat((pricings.subTotal * 0.10).toFixed(2)) : 0);
      const appliedDiscount = hasCoupon ? pricings.discount : 0;

      const commonPayload = {
        ...data,
        ...shippingData,
        cart: JSON.stringify(cart),
        quantity: pricings.quantity,
        couponCode: hasCoupon ? coupon?.code : null,
        AffiliateId: hasCoupon ? (coupon?.AffiliateId || null) : (refAff && pricings.subTotal >= 500 ? parseInt(refAff) : null),
        affiliatePoint: calculatedPoints,
        affiliateReward: calculatedReward,
        total: pricings.subTotal,
        discount: appliedDiscount,
        discountPercent: hasCoupon ? (coupon?.couponPercent || 0) : 0,
        gstAmount: pricings.gstAmount,
        shippingAmount: pricings.shippingCharge,
        finalAmount: pricings.totalAmount,
        returnUrl: process.env.REACT_APP_SITE_URL ? `${process.env.REACT_APP_SITE_URL}/payment-verify` : `${window.location.origin}/payment-verify`
      };

      if (data.orderType === "2") {
        createOrderCOD({
          ...commonPayload,
          sessionId: localStorage.getItem("bhabhuti-session"),
        });
      } else {
        pendingPricings.current = { ...pricings };
        pendingPayment.current = true;
        createOrder(commonPayload);
      }
    } else {
      setIsPaymentOn(true);
    }
  }

  // Calculate pricing from cart and coupon (GST added on top)
  useEffect(() => {
    if (cart.length > 0) {
      const subTotal = cart.reduce(
        (sum, item) => sum + item.quantity * item?.Product?.currentPrice,
        0,
      );
      const quantity = cart.reduce(
        (sum, item) => sum + parseInt(item.quantity),
        0,
      );

      const hasCoupon = coupon?.code && coupon.code === couponCode && subTotal >= 500;

      let discount = 0;
      if (hasCoupon) {
        if (coupon.type === 2) {
          discount = subTotal < (coupon.value || 0) ? subTotal : (coupon.value || coupon.discount || 0);
        } else {
          const percent = coupon.couponPercent || coupon.value || 0;
          const validAmount = subTotal * (percent / 100);
          discount = (coupon.upto && validAmount > coupon.upto) ? coupon.upto : validAmount;
        }
        if (discount > subTotal) discount = subTotal;
      }

      const couponPercent = hasCoupon ? (coupon?.couponPercent || coupon?.value || 0) : 0;

      const gstAmount = cart.reduce((sum, item) => {
        const productPrice = item?.Product?.currentPrice * item?.quantity;
        const productDiscount = productPrice * (couponPercent / 100);
        const productRealSellingPrice = Math.max(0, productPrice - productDiscount);
        const itemGst = productRealSellingPrice * (parseInt(item?.Product?.gst || 0) / 100);
        return sum + itemGst;
      }, 0);

      const shippingCharge = subTotal >= 500 ? 0 : 50;
      const totalAmount = Math.max(1, parseFloat((subTotal + shippingCharge - discount + gstAmount).toFixed(2)));

      setPricings({
        subTotal,
        quantity,
        discount: parseFloat(discount.toFixed(2)),
        shippingCharge,
        totalAmount,
        gstAmount,
      });
    } else {
      setPricings({
        subTotal: 0,
        quantity: 0,
        discount: 0,
        shippingCharge: 0,
        totalAmount: 0,
        gstAmount: 0,
      });
    }
  }, [cart, coupon, couponCode]);

  return (
    <>
      <div className="container my-5">
        <div className="col-md-12" style={{ padding: "15px 10px" }}>
          <p style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <i className="fa fa-arrow-left"></i> Go Back
          </p>
        </div>
        <div
          className="col-md-12"
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "15px 0px",
          }}
        >
          <h3>Checkout</h3>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className="row"
            style={{ borderTop: "1px solid #dadada", padding: "10px auto" }}
          >
            {/* Billing / Shipping Form */}
            <div className="col-md-7">
              <div className="card p-4">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <h4 className="fw-bold">Contact</h4>
                  <h6
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => navigate("/login?redirect_path=checkout")}
                  >
                    Login
                  </h6>
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    name="billingEmail"
                    {...register("billingEmail", {
                      required: "Email address is required",
                    })}
                    className="form-control"
                    id="billingEmail"
                    placeholder=" "
                  />
                  <label htmlFor="billingEmail">Email</label>
                  {errors.billingEmail && (
                    <p className="text-danger px-2">
                      {errors.billingEmail.message}
                    </p>
                  )}
                </div>

                {orders && orders.length > 0 && (
                  <div className="form-check my-3 p-3 bg-light border rounded d-flex align-items-center gap-2">
                    <input
                      className="form-check-input ms-1"
                      type="checkbox"
                      id="useLastOrderAddress"
                      onChange={(e) => handleUseLastAddress(e.target.checked)}
                      style={{ cursor: "pointer", width: "1.25rem", height: "1.25rem" }}
                    />
                    <label className="form-check-label fw-bold text-success mb-0" htmlFor="useLastOrderAddress" style={{ cursor: "pointer" }}>
                      Use address from previous order
                    </label>
                  </div>
                )}

                <h4 className="fw-bold">Billing</h4>

                <div className="row">
                  <div className="form-group">
                    <select
                      className="form-control"
                      id="billingCountry"
                      name="billingCountry"
                      {...register("billingCountry", {
                        required: "Country is required",
                      })}
                    >
                      <option value="" disabled hidden>
                        Choose Country
                      </option>
                      <option value="India">India</option>
                    </select>
                    {errors.billingCountry && (
                      <p className="text-danger px-2">
                        {errors.billingCountry.message}
                      </p>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="text"
                        name="billingName"
                        {...register("billingName", {
                          required: "Name is required",
                        })}
                        className="form-control"
                        id="billingName"
                        placeholder=" "
                      />
                      <label htmlFor="billingName">Name</label>
                      {errors.billingName && (
                        <p className="text-danger px-2">
                          {errors.billingName.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        name="billingPhone"
                        {...register("billingPhone", {
                          required: "Phone is required",
                        })}
                        id="billingPhone"
                        placeholder=" "
                      />
                      <label htmlFor="billingPhone">Phone</label>
                      {errors.billingPhone && (
                        <p className="text-danger px-2">
                          {errors.billingPhone.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="billingAddress"
                    {...register("billingAddress", {
                      required: "Address is required",
                    })}
                    className="form-control"
                    id="billingAddress"
                    placeholder=" "
                  />
                  <label htmlFor="billingAddress">Address</label>
                  {errors.billingAddress && (
                    <p className="text-danger px-2">
                      {errors.billingAddress.message}
                    </p>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <input
                        type="text"
                        name="billingCity"
                        {...register("billingCity", {
                          required: "City is required",
                        })}
                        className="form-control"
                        id="billingCity"
                        placeholder=" "
                      />
                      <label htmlFor="billingCity">City</label>
                      {errors.billingCity && (
                        <p className="text-danger px-2">
                          {errors.billingCity.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <select
                        className="form-control"
                        id="billingState"
                        name="billingState"
                        {...register("billingState", {
                          required: "State is required",
                        })}
                      >
                        <option value="" disabled hidden>
                          Choose State
                        </option>
                        <option value="Andaman and Nicobar Islands">
                          Andaman and Nicobar Islands
                        </option>
                        <option value="Andhra Pradesh">Andhra Pradesh</option>
                        <option value="Arunachal Pradesh">
                          Arunachal Pradesh
                        </option>
                        <option value="Assam">Assam</option>
                        <option value="Bihar">Bihar</option>
                        <option value="Chandigarh">Chandigarh</option>
                        <option value="Chhattisgarh">Chhattisgarh</option>
                        <option value="Dadra and Nagar Haveli and Daman & Diu">
                          Dadra and Nagar Haveli and Daman & Diu
                        </option>
                        <option value="Goa">Goa</option>
                        <option value="Gujarat">Gujarat</option>
                        <option value="Haryana">Haryana</option>
                        <option value="Himachal Pradesh">
                          Himachal Pradesh
                        </option>
                        <option value="Jammu and Kashmir">
                          Jammu and Kashmir
                        </option>
                        <option value="Jharkhand">Jharkhand</option>
                        <option value="Karnataka">Karnataka</option>
                        <option value="Kerala">Kerala</option>
                        <option value="National Capital Territory of Delhi">
                          National Capital Territory of Delhi
                        </option>
                        <option value="Ladakh">Ladakh</option>
                        <option value="Lakshadweep">Lakshadweep</option>
                        <option value="Madhya Pradesh">Madhya Pradesh</option>
                        <option value="Maharashtra">Maharashtra</option>
                        <option value="Manipur">Manipur</option>
                        <option value="Meghalaya">Meghalaya</option>
                        <option value="Mizoram">Mizoram</option>
                        <option value="Nagaland">Nagaland</option>
                        <option value="Odisha">Odisha</option>
                        <option value="Puducherry">Puducherry</option>
                        <option value="Punjab">Punjab</option>
                        <option value="Rajasthan">Rajasthan</option>
                        <option value="Sikkim">Sikkim</option>
                        <option value="Tamil Nadu">Tamil Nadu</option>
                        <option value="Telangana">Telangana</option>
                        <option value="Tripura">Tripura</option>
                        <option value="Uttar Pradesh">Uttar Pradesh</option>
                        <option value="Uttarakhand">Uttarakhand</option>
                        <option value="West Bengal">West Bengal</option>
                      </select>
                      {errors.billingState && (
                        <p className="text-danger px-2">
                          {errors.billingState.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <input
                        type="text"
                        name="billingZip"
                        {...register("billingZip", {
                          required: "Pin code is required",
                        })}
                        className="form-control"
                        id="billingZip"
                        placeholder=" "
                      />
                      <label htmlFor="billingZip">PIN Code</label>
                      {errors.billingZip && (
                        <p className="text-danger px-2">
                          {errors.billingZip.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveInfo"
                    onChange={() => setIsShipping(!isShipping)}
                  />
                  <label className="form-check-label" htmlFor="saveInfo">
                    Is shipping address different than billing address?
                  </label>
                </div>
              </div>

              {isShipping && (
                <div className="card p-4">
                  <h4 className="fw-bold">Shipping</h4>
                  <div className="row">
                    <div className="col-md-6 form-group">
                      <select
                        className="form-control"
                        id="shippingCountry"
                        name="shippingCountry"
                        {...register("shippingCountry", {
                          required: "Country is required",
                        })}
                      >
                        <option value="" disabled hidden>
                          Choose Country
                        </option>
                        <option value="India">India</option>
                      </select>
                      {errors.shippingCountry && (
                        <p className="text-danger px-2">
                          {errors.shippingCountry.message}
                        </p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="shippingName"
                          {...register("shippingName", {
                            required: "Name is required",
                          })}
                          className="form-control"
                          id="shippingName"
                          placeholder=" "
                        />
                        <label htmlFor="shippingName">Name</label>
                        {errors.shippingName && (
                          <p className="text-danger px-2">
                            {errors.shippingName.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      name="shippingAddress"
                      {...register("shippingAddress", {
                        required: "Address is required",
                      })}
                      className="form-control"
                      id="shippingAddress"
                      placeholder=" "
                    />
                    <label htmlFor="shippingAddress">Address</label>
                    {errors.shippingAddress && (
                      <p className="text-danger px-2">
                        {errors.shippingAddress.message}
                      </p>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-4">
                      <div className="form-group">
                        <input
                          type="text"
                          name="shippingCity"
                          {...register("shippingCity", {
                            required: "City is required",
                          })}
                          className="form-control"
                          id="shippingCity"
                          placeholder=" "
                        />
                        <label htmlFor="shippingCity">City</label>
                        {errors.shippingCity && (
                          <p className="text-danger px-2">
                            {errors.shippingCity.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group">
                        <select
                          className="form-control"
                          id="shippingState"
                          name="shippingState"
                          {...register("shippingState", {
                            required: "State is required",
                          })}
                        >
                          <option value="" disabled hidden>
                            Choose State
                          </option>
                          <option value="Andaman and Nicobar Islands">
                            Andaman and Nicobar Islands
                          </option>
                          <option value="Andhra Pradesh">Andhra Pradesh</option>
                          <option value="Arunachal Pradesh">
                            Arunachal Pradesh
                          </option>
                          <option value="Assam">Assam</option>
                          <option value="Bihar">Bihar</option>
                          <option value="Chandigarh">Chandigarh</option>
                          <option value="Chhattisgarh">Chhattisgarh</option>
                          <option value="Dadra and Nagar Haveli and Daman & Diu">
                            Dadra and Nagar Haveli and Daman & Diu
                          </option>
                          <option value="Goa">Goa</option>
                          <option value="Gujarat">Gujarat</option>
                          <option value="Haryana">Haryana</option>
                          <option value="Himachal Pradesh">
                            Himachal Pradesh
                          </option>
                          <option value="Jammu and Kashmir">
                            Jammu and Kashmir
                          </option>
                          <option value="Jharkhand">Jharkhand</option>
                          <option value="Karnataka">Karnataka</option>
                          <option value="Kerala">Kerala</option>
                          <option value="National Capital Territory of Delhi">
                            National Capital Territory of Delhi
                          </option>
                          <option value="Ladakh">Ladakh</option>
                          <option value="Lakshadweep">Lakshadweep</option>
                          <option value="Madhya Pradesh">Madhya Pradesh</option>
                          <option value="Maharashtra">Maharashtra</option>
                          <option value="Manipur">Manipur</option>
                          <option value="Meghalaya">Meghalaya</option>
                          <option value="Mizoram">Mizoram</option>
                          <option value="Nagaland">Nagaland</option>
                          <option value="Odisha">Odisha</option>
                          <option value="Puducherry">Puducherry</option>
                          <option value="Punjab">Punjab</option>
                          <option value="Rajasthan">Rajasthan</option>
                          <option value="Sikkim">Sikkim</option>
                          <option value="Tamil Nadu">Tamil Nadu</option>
                          <option value="Telangana">Telangana</option>
                          <option value="Tripura">Tripura</option>
                          <option value="Uttar Pradesh">Uttar Pradesh</option>
                          <option value="Uttarakhand">Uttarakhand</option>
                          <option value="West Bengal">West Bengal</option>
                        </select>
                        {errors.shippingState && (
                          <p className="text-danger px-2">
                            {errors.shippingState.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group">
                        <input
                          type="text"
                          name="shippingZip"
                          {...register("shippingZip", {
                            required: "Pin code is required",
                          })}
                          className="form-control"
                          id="shippingZip"
                          placeholder=" "
                        />
                        <label htmlFor="shippingZip">PIN Code</label>
                        {errors.shippingZip && (
                          <p className="text-danger px-2">
                            {errors.shippingZip.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="shippingPhone"
                          {...register("shippingPhone", {
                            required: "Phone is required",
                          })}
                          id="shippingPhone"
                          placeholder=" "
                        />
                        <label htmlFor="shippingPhone">Phone</label>
                        {errors.shippingPhone && (
                          <p className="text-danger px-2">
                            {errors.shippingPhone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <input
                          type="email"
                          className="form-control"
                          name="shippingEmail"
                          {...register("shippingEmail", {
                            required: "Email is required",
                          })}
                          id="shippingEmail"
                          placeholder=" "
                        />
                        <label htmlFor="shippingEmail">Email</label>
                        {errors.shippingEmail && (
                          <p className="text-danger px-2">
                            {errors.shippingEmail.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div
              className="col-md-5 checkout-summary-col"
            >
              <div className="card p-4">
                {Array.isArray(cart) &&
                  cart.map((x) => (
                    <div
                      className="d-flex justify-content-between align-items-center mb-2"
                      key={x.id}
                    >
                      <div className="d-flex align-items-center position-relative">
                        <img
                          src={`${process.env.REACT_APP_ASSET_URL}/${x?.Product?.image}`}
                          alt={x?.Product?.name}
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "10px",
                          }}
                        />
                        <span
                          className="badge bg-secondary position-absolute top-0 start-0 translate-middle"
                          style={{
                            fontSize: "0.75rem",
                            padding: "0.25rem 0.4rem",
                          }}
                        >
                          {x?.quantity}
                        </span>
                        <span className="ms-2">{x?.Product?.name}</span>
                      </div>
                      <span>₹{x?.Product?.currentPrice * x?.quantity}</span>
                    </div>
                  ))}

                <div className="input-group mt-4">
                  <input
                    type="text"
                    className="form-control discount-input"
                    placeholder="Discount code or gift card"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button
                    className="btn btn-primary ms-2 discount-apply"
                    type="button"
                    onClick={() => {
                      if (couponCode === "") {
                        setCodeErr("Please add a code.");
                      } else if (pricings?.subTotal < 500) {
                        setCodeErr("Minimum order amount of ₹500 is required to apply a coupon.");
                      } else {
                        setCodeErr(null);
                        applyCoupon({
                          code: couponCode,
                          amount: pricings?.subTotal,
                        });
                      }
                    }}
                  >
                    Apply
                  </button>
                </div>
                {codeErr && <p className="text-danger px-2">{codeErr}</p>}

                <hr />
                <div className="d-flex justify-content-between">
                  <span>Subtotal ({pricings.quantity} items)</span>
                  <span>₹{pricings.subTotal}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Shipping</span>
                  <span>₹{pricings.shippingCharge}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>GST Charges</span>
                  <span>₹{pricings.gstAmount.toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span>Discount</span>
                  <span>₹{pricings.discount}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span style={{ fontSize: "25px" }}>Total</span>
                  <span style={{ fontSize: "25px" }}>
                    ₹{pricings.totalAmount}
                  </span>
                </div>

                {isPaymentOn ? (
                  <>
                    <div style={{ marginTop: "20px" }}>
                      <h5>Payment Method</h5>
                    </div>
                    <div className="radio-group d-flex gap-3">
                      <div className="form-check">
                        <input
                          {...register("orderType")}
                          className="form-check-input"
                          type="radio"
                          id="cod"
                          value="2"
                        />
                        <label className="form-check-label" htmlFor="cod">
                          COD
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          {...register("orderType")}
                          className="form-check-input"
                          type="radio"
                          id="rzr"
                          value="1"
                        />
                        <label className="form-check-label" htmlFor="rzr">
                          Online Pay
                        </label>
                      </div>
                    </div>
                    <button
                      type="submit"
                      style={{ width: "100%", marginTop: "20px" }}
                      className="btn btn-success"
                    >
                      Complete Order
                    </button>
                  </>
                ) : (
                  <button
                    type="submit"
                    style={{ width: "100%", marginTop: "20px" }}
                    className="btn btn-success"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
